import React from "react";
import { StyledTypographyDesc } from "./heroSection.style";
import parse from 'html-react-parser';
import Typography from "component/MarketPlace/Typography/Typography";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Button } from "../ButtonComponents";
import HealthyChip from 'component/commonComponents/HealthyChip';
import { PerformanceConfig } from "models/Device";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import { Box, CircularProgress, Stack } from "@mui/material";
import { useTheme } from "styled-components";
import { NOT_APPLICABLE } from '../../../constants/originalState';
import { mapTextId } from "utils/mapTextId";
import HeroSectionSkeleton from "./skeleton";
import UptimeImage from "component/commonComponents/UptimeImage/UptimeImage";

export interface IHeroSectionProps {
  overLine?: string;
  header?: string;
  desc?: string;
  handleView?: () => void;
  labelBtn?: string;
  placeHolderImg?: any;
  banner?: any;
  isMyTeam?: boolean;
  serialNumber?: string;
  healthStatus?: any;
  handleMoreAction?: (item?: any) => void;
  deviceDetail: any;
  locale?: any;
  onClickAction?: (item?: any, itemId?: string) => void;
  isCheckTachyon?: boolean;
  performanceConfig: PerformanceConfig;
  performance?: string;
  localePerformance?: any;
  virtualDeviceId?: string;
  loadingRelease?: boolean;
};

const HeroSection = (props: IHeroSectionProps) => {
  const {
    overLine,
    header,
    desc,
    labelBtn,
    handleView,
    placeHolderImg,
    banner,
    isMyTeam = false,
    handleMoreAction = () => {},
    deviceDetail = {},
    locale,
    onClickAction = () => {},
    isCheckTachyon,
    performanceConfig,
    performance,
    localePerformance,
    virtualDeviceId,
    loadingRelease
  } = props;
  const checkBanner = banner ? true : false;

  let arrActions = Object.keys(deviceDetail?.actions || {});
  const textActionsLabel = arrActions?.find(
    (action: any) => deviceDetail?.actions?.[action]
  );

  const isMarketplaceLandingPage = !virtualDeviceId && !isMyTeam;

  const theme = useTheme();

  return (
    <Box
      display="flex"
      flexDirection={{
        xs: "column",
        sm: "column",
        md: "row",
        lg: "row",
        xl: "row",
      }}
      p={`0 4px`}
    >
      <Box
        display="flex"
        flexDirection={{
          xs: "column-reverse",
          sm: "column-reverse",
          md: "row",
          lg: "row",
          xl: "row",
        }}
        width="100%"
        sx={{padding: isMyTeam || virtualDeviceId ? '48px 0px' : '0px'}}
      >
        <Stack
          width={{
            xs: "100%",
            sm: "100%",
            md: "50%",
            lg: "50%",
            xl: "50%",
          }}
          mt={{
            xs: "40px",
            md: 0,
          }}
        >
          <Typography
            variant="overline"
            fontWeight="700"
            textTransform="uppercase"
            sx={{
              color: "primary.main",
            }}
          >
            {overLine}
          </Typography>
          <Typography fontWeight="600" variant="h5">
            {header || NOT_APPLICABLE}
          </Typography>
          <StyledTypographyDesc
            variant="body2"
            sx={{
              mt: `8px`,
            }}
          >
            {isMyTeam ? (
              <Box sx={{ display: "flex", alignSelf: "center" }}>
                <Box sx={{ display: "flex", alignSelf: "center" }}>
                  <LocalOfferIcon
                    sx={{ height: "20px", width: "20px", mr: 1 }}
                  />
                </Box>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  fontWeight={700}
                  lineHeight="32px"
                  mr={1}
                >
                  {locale?.serialNumber}:
                </Typography>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  fontWeight={700}
                  lineHeight="32px"
                >
                  {desc}
                </Typography>
              </Box>
            ) : (
              virtualDeviceId ? 
              <Box sx={{ display: "flex", alignSelf: "center", mb: '24px' }}>
                <Box sx={{ display: "flex", alignSelf: "center" }}>
                  <BadgeOutlinedIcon
                    sx={{ height: "20px", width: "20px", mr: 1 }}
                  />
                </Box>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  fontWeight={700}
                  lineHeight="32px"
                  mr={1}
                >
                  {locale?.virtualDevice?.description}:
                </Typography>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  fontWeight={700}
                  lineHeight="32px"
                >
                  {desc}
                </Typography>
              </Box> : parse(desc!)
            )}
          </StyledTypographyDesc>

          {isMyTeam && isCheckTachyon && (
            <StyledTypographyDesc
              variant="body2"
              sx={{
                mt: `8px`,
                mb: 3
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box sx={{ display: "flex", alignSelf: "center" }}>
                  <CheckCircleOutlineIcon
                    sx={{ height: "20px", width: "20px", mr: 1, mt: "-2px" }}
                  />
                </Box>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  fontWeight={700}
                  lineHeight="32px"
                  mr={1}
                >
                  {locale?.deviceHealth}:
                </Typography>
                {performance && (
                  <HealthyChip
                    performanceConfig={performanceConfig}
                    performance={performance}
                    localeText={localePerformance}
                  />
                )}
              </Box>
            </StyledTypographyDesc>
          )}
          {isMyTeam && (
            <Box sx={{ display: "flex" }}>
              {textActionsLabel && locale?.textActions?.[textActionsLabel] && (
                <Button
                  sx={{ mr: 4 }}
                  onClick={() => onClickAction(textActionsLabel, deviceDetail)}
                  id={`btn-herosection-${mapTextId(textActionsLabel)}`}
                >
                  {locale?.textActions?.[textActionsLabel]}
                </Button>
              )}
              <Button
                onClick={() => handleMoreAction(deviceDetail)}
                endIcon={<ChevronRightIcon />}
                variant="text"
                sx={{
                  color: theme.uptimeColors?.generalColors?.button?.hyperlink?.enabled,
                  '&:hover': {
                    color: theme.uptimeColors?.generalColors?.button?.hyperlink?.hover,
                  }
                }}
                id="btn-herosection-more"
              >
                {locale?.moreBtn}
              </Button>
            </Box>
          )}
          {
            virtualDeviceId && (
              <Box sx={{ display: "flex" }}>
                <Button
                  sx={{ mr: 4 }}
                  startIcon={loadingRelease && <CircularProgress sx={{ color: 'white'}} size={14}/>}
                  onClick={() => onClickAction(deviceDetail?.aadGroupName)}
                >
                  {locale?.virtualDevice?.releaseVirtualDesktop}
                </Button>
                <Button
                  onClick={() => handleMoreAction(deviceDetail)}
                  endIcon={<ChevronRightIcon />}
                  variant="text"
                  sx={{
                    color: theme.uptimeColors?.generalColors?.button?.hyperlink?.enabled,
                    '&:hover': {
                      color: theme.uptimeColors?.generalColors?.button?.hyperlink?.hover,
                    }
                  }}
                  id="btn-herosection-more"
                >
                  {locale?.moreBtn}
                </Button>
              </Box>
            )
          }
          {isMarketplaceLandingPage &&
            <Box width={{ xs: "100%", md: "280px", marginTop: "auto" }}>
              <Button onClick={handleView} sx={{ padding: "8px 22px" }} fullWidth>
                {labelBtn}
              </Button>
            </Box>
          }
        </Stack>
        <Box
          width={{
            xs: "100%",
            sm: "100%",
            md: "50%",
            lg: "50%",
            xl: "50%",
          }}
          display="flex"
          alignItems="center"
        >
          { typeof placeHolderImg === "string" ? 
            <UptimeImage
              alt="image-hero-section"
              title="Image Hero Section"
              src={!checkBanner ? placeHolderImg : ""}
              placeHolderImg={placeHolderImg}
              height={250}
              width="100%"
              wrapperProps={{
                style: {
                  display: "inline",
                  height: placeHolderImg ? "250px" : "auto",
                  minHeight: "200px",
                  objectFit: "scale-down",
                },
              }}
            />  : placeHolderImg
          }
        </Box>
      </Box>
    </Box>
  );
};

HeroSection.Skeleton = HeroSectionSkeleton;

export default HeroSection;
